import type { ComponentType, ReactNode } from 'react'
import { PageBootSkeleton } from '@/components/layout/PageBootSkeleton'
import { getModuleLandingConfig } from '@/components/subscription/upgrade-landing/landingConfigs'
import { ModuleUpgradeLanding } from '@/components/subscription/upgrade-landing/ModuleUpgradeLanding'
import { useSubscriptionFeatures } from '@/hooks/useSubscriptionFeatures'
import { useSupplierTerminology } from '@/hooks/useSupplierTerminology'

interface SubscriptionFeatureGuardProps {
  featureKey: string
  children: ReactNode
  /**
   * @deprecated Kept for compatibility with existing route definitions.
   * The guard now renders a per-module upgrade landing instead of redirecting.
   */
  fallbackPath?: string
}

/**
 * Route guard component that checks if a subscription feature is enabled.
 * If the feature is not enabled, renders the matching upgrade-landing page so
 * Free users can see what the module offers and upgrade — instead of being
 * silently redirected away or shown a generic blocked card.
 */
export function SubscriptionFeatureGuard({
  featureKey,
  children,
}: SubscriptionFeatureGuardProps) {
  const { checkFeature, isLoading } = useSubscriptionFeatures()
  const supplierTerminology = useSupplierTerminology()

  // A page in flight keeps the shape of the page it becomes, never a bare
  // centred spinner (design audit S12).
  if (isLoading) {
    return <PageBootSkeleton />
  }

  if (!checkFeature(featureKey)) {
    return (
      <ModuleUpgradeLanding
        config={getModuleLandingConfig(featureKey, { supplierTerminology })}
        featureKey={featureKey}
      />
    )
  }

  return <>{children}</>
}

/**
 * HOC to wrap a component with subscription feature guard
 */
export function withSubscriptionFeature<P extends object>(
  Component: ComponentType<P>,
  featureKey: string,
  fallbackPath?: string,
) {
  return function WrappedComponent(props: P) {
    return (
      <SubscriptionFeatureGuard
        featureKey={featureKey}
        fallbackPath={fallbackPath}
      >
        <Component {...props} />
      </SubscriptionFeatureGuard>
    )
  }
}
